import type {
    ChatAgUiStateChange,
    ChatSnapshot,
    ChatStatusStateChange,
} from './chat-state';
import type {ChatStatusItem, PageChangeSetStatusItem} from './chat-status';
import {isWorkspaceContextUploadStatusItem} from './chat-status';
import {isRetryableError, offersUploadOf, suggestedAnswersOf} from './chat-suggestions';
import {ChangeSetCards, type PageChangeSetActions, type PageChangeSetLabels} from './chat-ui/change-set-cards';
import {ChangeConfirmations} from './chat-ui/change-confirmations';
import {PagePreview} from './chat-ui/page-preview';
import {ChatScrollController} from './chat-ui/scroll-controller';
import {ChatStatusArea} from './chat-ui/status-area';
import {ChatTranscriptRenderer} from './chat-ui/transcript-renderer';

declare const howspace: any;

type Labels = {
    placeholder: string;
    send: string;
    emptyState: string;
    thinking: string;
    thoughtFor: string;
    fewSeconds: string;
    seconds: string;
    copy: string;
    close: string;
    expandStatus: string;
    collapseStatus: string;
    toolCalls: string;
    toolStatusRunning: string;
    toolStatusDone: string;
    toolStatusError: string;
    toolArguments: string;
    toolResult: string;
    modelChangedWarning: string;
    threadStaleWarning: string;
    runTimeoutWarning: string;
    inputContextTooLargeWarning: string;
    queueDelayNotice: string;
    runStillQueuedNotice: string;
    thinkingWords: string;
    stillWorking: string;
    error: string;
    tryAgain: string;
    addContextFile: string;
    contextFileQueued: string;
    contextFileProcessing: string;
    contextFileReady: string;
    contextFileFailed: string;
    contextFileRemove: string;
    contextFileRemoveConfirm: string;
    contextFileRemoveConfirmAction: string;
    contextFileKeep: string;
    contextFileRemoveFailed: string;
    contextFileGeneratePageAction: string;
    contextFileCreateWidgetAction: string;
    contextFileMaxSourceFileSize: string;
    contextFileMaxCreatedAssetCount: string;
    contextFileMaxCreatedAssetFileSize: string;
    composerNote: string;
    emptyStateDescription: string;
};

export type ChatUiOptions = {
    labels: Labels;
    showReasoning: boolean;
    showToolCalls: boolean;
    showFriendlyToolStatus: boolean;
    toolLabels: Record<string, string>;
    modelMismatch: boolean;
    workspaceContextUpload: boolean;
    contextFileQuickActions: boolean;
    openingQuestion?: string;
    /** Why the chat cannot send. Empty while it can. */
    pausedNotice?: string;
    onSubmit: (message: string) => void;
    onAddContextFile?: (file?: File) => void;
    onRemoveContextFile?: (contextFileId: string) => Promise<boolean>;
    /** Present for a chat that stages page changes for the user to review. */
    changeSets?: {
        labels: PageChangeSetLabels;
        actions: PageChangeSetActions;
        currentPageId: () => string;
        /** Whether staged changes are previewed on the page now. */
        previewShown: () => boolean;
    };
};

export class ChatUi {
    private readonly root: HTMLElement;
    private readonly messages: HTMLElement;
    private readonly form: HTMLFormElement;
    private readonly textarea: HTMLTextAreaElement;
    private readonly submitButton: HTMLButtonElement;
    private readonly suggestions: HTMLElement;
    private readonly addContextButton: HTMLButtonElement | null = null;
    private readonly statusArea: ChatStatusArea;
    private readonly scrollController: ChatScrollController;
    private readonly transcriptRenderer: ChatTranscriptRenderer;
    private readonly changeSetCards: ChangeSetCards | null = null;
    private readonly pagePreview: PagePreview | null = null;
    private readonly confirmations = new ChangeConfirmations();
    private running = false;
    private lastStatusItems: ChatStatusItem[] = [];
    private contextUploadBusy = false;
    private paused = false;
    private pausedNotice: HTMLElement | null = null;

    constructor(container: HTMLElement, private readonly options: ChatUiOptions) {
        this.root = document.createElement('div');
        this.root.className = 'howspace-ai-chat';

        if (options.modelMismatch) {
            const warning = document.createElement('div');
            warning.className = 'howspace-ai-chat-warning';

            const warningText = document.createElement('span');
            warningText.className = 'howspace-ai-chat-warning-text';
            warningText.textContent = options.labels.modelChangedWarning;

            const closeLabel = options.labels.close || 'Close';
            const closeButton = document.createElement('button');
            closeButton.type = 'button';
            closeButton.className = 'howspace-ai-chat-warning-close';
            closeButton.setAttribute('aria-label', closeLabel);
            closeButton.title = closeLabel;
            closeButton.innerHTML = '<i class="fa-light fa-xmark" aria-hidden="true"></i>';
            closeButton.addEventListener('click', () => {
                warning.remove();
            });

            warning.append(warningText, closeButton);
            this.root.append(warning);
        }

        this.messages = document.createElement('div');
        this.messages.className = 'howspace-ai-chat-messages';
        this.messages.setAttribute('aria-live', 'polite');
        this.root.append(this.messages);
        this.scrollController = new ChatScrollController(this.messages);

        const status = document.createElement('div');
        status.className = 'howspace-ai-chat-status';
        status.setAttribute('aria-live', 'polite');
        this.root.append(status);
        this.statusArea = new ChatStatusArea(status, {
            labels: this.options.labels,
            isRunning: () => this.running,
            onSubmit: message => this.options.onSubmit(message),
            focusComposer: () => this.focusComposer(),
            setContextUploadBusy: busy => this.setContextUploadBusy(busy),
            contextFileQuickActions: this.options.contextFileQuickActions,
            onRemoveContextFile: this.options.onRemoveContextFile,
        });
        this.transcriptRenderer = new ChatTranscriptRenderer(this.messages, this.scrollController, {
            labels: this.options.labels,
            showReasoning: this.options.showReasoning,
            showToolCalls: this.options.showToolCalls,
            showFriendlyToolStatus: this.options.showFriendlyToolStatus,
            toolLabels: this.options.toolLabels,
            openingQuestion: this.options.openingQuestion,
            isRunning: () => this.running,
        });

        if (options.changeSets) {
            const cards = document.createElement('div');
            this.root.append(cards);
            const changeSets = options.changeSets;
            const preview = new PagePreview({
                labels: changeSets.labels,
                currentPageId: changeSets.currentPageId,
                isShown: changeSets.previewShown,
                isBlocked: () => this.running || (this.changeSetCards?.isBusy() ?? false),
                skip: (set, operationId) => this.changeSetCards?.skipFromPage(set, operationId),
                confirmations: this.confirmations,
                onPageChanged: () => this.changeSetCards?.recheckStaged(),
            });
            this.pagePreview = preview;
            this.changeSetCards = new ChangeSetCards(cards, {
                labels: changeSets.labels,
                actions: changeSets.actions,
                currentPageId: changeSets.currentPageId,
                isRunning: () => this.running,
                close: options.labels.close,
                reveal: (set, operation) => preview.reveal(set, operation),
                onBusyChange: () => preview.syncDisabled(),
                confirmations: this.confirmations,
                focusComposer: () => this.textarea.focus(),
            });
            this.confirmations.onChange(() => {
                this.changeSetCards?.refresh();
                this.pagePreview?.syncConfirmations();
            });
        }

        // Between the transcript and the composer, where an answer is about to be typed. Everything the chat
        // offers as a button lives here: the answers to the question just asked, and the way out of an error.
        this.suggestions = document.createElement('div');
        this.suggestions.className = 'howspace-ai-chat-suggestions';
        this.root.append(this.suggestions);

        if (this.options.labels.composerNote) {
            const note = document.createElement('p');
            note.className = 'howspace-ai-chat-composer-note';
            note.textContent = this.options.labels.composerNote;
            this.root.append(note);
        }

        this.form = document.createElement('form');
        this.form.className = 'howspace-ai-chat-composer';

        const inputShell = document.createElement('div');
        inputShell.className = 'howspace-ai-chat-composer-input';
        if (options.workspaceContextUpload) {
            this.addContextButton = this.renderAddContextButton();
        }

        this.textarea = document.createElement('textarea');
        this.textarea.className = 'howspace-ai-chat-input';
        this.textarea.rows = 1;
        this.textarea.placeholder = options.labels.placeholder;
        this.textarea.setAttribute('aria-label', options.labels.placeholder);

        this.submitButton = document.createElement('button');
        this.submitButton.type = 'submit';
        this.submitButton.className = 'howspace-ai-chat-send';
        this.submitButton.setAttribute('aria-label', options.labels.send);
        this.submitButton.title = options.labels.send;
        this.submitButton.innerHTML =
            '<i class="fa-light fa-arrow-down howspace-ai-chat-send-icon" aria-hidden="true"></i>';

        // The textarea scrolls on its own row so long messages never slide under the buttons, which sit on
        // a static row below it.
        const actionsRow = document.createElement('div');
        actionsRow.className = 'howspace-ai-chat-composer-actions';
        if (this.addContextButton !== null) {
            actionsRow.append(this.addContextButton);
        }
        actionsRow.append(this.submitButton);

        inputShell.append(this.textarea, actionsRow);
        this.form.append(inputShell);
        this.root.append(this.form);
        container.prepend(this.root);

        this.form.addEventListener('submit', event => {
            event.preventDefault();
            const message = this.textarea.value.trim();
            if (!message || this.running || this.submitButton.disabled) {
                return;
            }
            // Sending sticks the transcript to the bottom even if the user had scrolled up to read. It has to
            // happen before the message is handed over, because that renders it and samples the scroll state.
            this.scrollToBottom();
            this.options.onSubmit(message);
            this.textarea.value = '';
            this.resizeTextarea();
            this.updateSubmitState();
        });

        this.textarea.addEventListener('input', () => {
            this.resizeTextarea();
            this.updateSubmitState();
        });

        this.textarea.addEventListener('keydown', event => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.form.requestSubmit();
            }
        });

        if (options.workspaceContextUpload) {
            this.root.addEventListener('dragover', this.handleFileDragOver);
            this.root.addEventListener('dragenter', this.handleFileDragOver);
            this.root.addEventListener('dragleave', this.handleFileDragLeave);
            this.root.addEventListener('drop', this.handleFileDrop);
        }

        this.resizeTextarea();
        this.updateSubmitState();
        if (options.pausedNotice) {
            this.pause(options.pausedNotice);
        }
        this.focusComposer(true);
    }

    private renderAddContextButton(): HTMLButtonElement {
        const label = this.options.labels.addContextFile || 'Add context for the AI';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-chat-add-context';
        button.setAttribute('aria-label', label);

        const icon = document.createElement('span');
        icon.className = 'howspace-ai-chat-add-context-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '+';

        const tooltip = document.createElement('span');
        tooltip.className = 'howspace-ai-chat-add-context-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.textContent = label;

        button.append(icon, tooltip);
        button.addEventListener('click', () => {
            if (button.disabled || this.contextUploadBusy || this.running) {
                return;
            }
            this.options.onAddContextFile?.();
        });
        return button;
    }

    /**
     * The buttons on offer right now: the answers the agent suggested for the question it just asked, and
     * after a failed run, the retry.
     *
     * Nothing is offered while a run is going, because every one of them sends a message.
     *
     * The retry is a chip like any other, so it sends a message like any other. That is the whole design:
     * people whose run timed out typed "Try again" into the chat themselves and the agent picked up where it
     * left off, so the chip is a shortcut for those keystrokes and not a separate replay mechanism. Sending
     * the words is correct behavior, not a placeholder for one.
     */
    private renderSuggestions(source: {state: unknown, running: boolean, errorCode: string | null}): void {
        const chips = source.running || this.paused ? [] : [
            ...suggestedAnswersOf(source.state),
            ...(isRetryableError(source.errorCode) && this.options.labels.tryAgain
                ? [this.options.labels.tryAgain]
                : []),
        ];

        // An upload offered with the question opens the upload instead of sending anything, so it stays on
        // screen after the upload and the answer the agent suggested beside it can still be picked.
        const upload = !source.running && !this.paused && offersUploadOf(source.state)
            && this.options.workspaceContextUpload
            ? [this.renderUploadSuggestion()]
            : [];

        this.suggestions.replaceChildren(...upload, ...chips.map(text => this.renderSuggestion(text)));
        this.suggestions.hidden = upload.length + chips.length === 0;
    }

    private renderUploadSuggestion(): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-chat-suggestion is-upload';
        button.textContent = this.options.labels.addContextFile;
        button.disabled = this.contextUploadBusy;
        button.addEventListener('click', () => {
            if (this.running || this.contextUploadBusy) {
                return;
            }
            this.options.onAddContextFile?.();
        });

        return button;
    }

    private renderSuggestion(text: string): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-chat-suggestion';
        button.textContent = text;
        button.addEventListener('click', () => {
            if (this.running || isActivityIdle()) {
                return;
            }
            // Sent as the user's own message, so it pins the transcript exactly as submitting does. The
            // buttons go now rather than waiting for the run to report itself.
            this.suggestions.replaceChildren();
            this.suggestions.hidden = true;
            this.scrollToBottom();
            // The chip carries whatever the composer holds, because a failure that put the message back
            // there is a failure that needs that message sent again. With nothing to carry it sends its
            // own words, which the agent reads as the nudge a user would otherwise have typed.
            const draft = this.textarea.value.trim();
            if (text === this.options.labels.tryAgain && draft !== '') {
                this.textarea.value = '';
                this.resizeTextarea();
                this.options.onSubmit(draft);
                return;
            }
            this.options.onSubmit(text);
        });

        return button;
    }

    private readonly handleFileDragOver = (event: DragEvent): void => {
        if (!this.isFileDrag(event)) {
            return;
        }

        event.preventDefault();
        if (this.canAcceptFileDrop(event)) {
            this.root.classList.add('is-file-drag-over');
        } else {
            this.root.classList.remove('is-file-drag-over');
        }
    };

    private readonly handleFileDragLeave = (event: DragEvent): void => {
        if (event.relatedTarget instanceof Node && this.root.contains(event.relatedTarget)) {
            return;
        }

        this.root.classList.remove('is-file-drag-over');
    };

    private readonly handleFileDrop = (event: DragEvent): void => {
        if (!this.isFileDrag(event)) {
            return;
        }

        event.preventDefault();
        this.root.classList.remove('is-file-drag-over');
        if (!this.canAcceptFileDrop(event)) {
            return;
        }

        const file = event.dataTransfer?.files?.[0] || null;
        if (file instanceof File) {
            this.options.onAddContextFile?.(file);
        }
    };

    private canAcceptFileDrop(event: DragEvent): boolean {
        return this.options.workspaceContextUpload
            && !this.contextUploadBusy
            && !this.running
            && event.dataTransfer !== null
            && (event.dataTransfer.files.length === 0 || event.dataTransfer.files.length === 1);
    }

    private isFileDrag(event: DragEvent): boolean {
        return event.dataTransfer !== null
            && Array.from(event.dataTransfer.types).includes('Files');
    }

    // Bootstrap/replay can build the whole transcript. Streaming updates must
    // go through applyChange so completed history stays inert.
    renderInitial(snapshot: ChatSnapshot): void {
        this.setRunning(snapshot.running);
        this.statusArea.render(snapshot);
        this.setContextMaterialPresent(snapshot.statusItems);
        this.renderChangeSets(snapshot.statusItems);
        this.renderSuggestions(snapshot);
        this.updateSubmitState();

        if (this.transcriptRenderer.renderInitial(snapshot)) {
            this.scrollToBottom();
        }
    }

    applyAgUiChange(change: ChatAgUiStateChange | null): void {
        if (!change) {
            return;
        }

        const shouldFollowBottom = this.scrollController.shouldFollowBottom();
        this.setRunning(change.running);
        this.statusArea.render(change);
        this.setContextMaterialPresent(change.statusItems);
        this.renderChangeSets(change.statusItems);
        this.transcriptRenderer.applyChange(change);
        this.renderSuggestions(change);

        this.updateSubmitState();
        if (change.event.type === 'RUN_FINISHED' || change.event.type === 'RUN_ERROR') {
            this.focusComposer();
        }
        // Following the bottom is not only about the transcript growing. The status area below it takes its
        // height from the same box, so it pushes the newest message out of view as it fills up.
        if (shouldFollowBottom) {
            this.scrollToBottom();
        }
    }

    applyStatusChange(change: ChatStatusStateChange | null): void {
        if (!change) {
            return;
        }

        const shouldFollowBottom = this.scrollController.shouldFollowBottom();
        this.setRunning(change.running);
        this.statusArea.render(change);
        this.setContextMaterialPresent(change.statusItems);
        this.renderChangeSets(change.statusItems);
        this.renderSuggestions(change);
        this.updateSubmitState();
        if (shouldFollowBottom) {
            this.scrollToBottom();
        }
    }

    /**
     * Whether a change set of this chat is being applied. Its progress and outcome reach only this chat.
     */
    isApplyingChanges(): boolean {
        return this.changeSetCards?.isApplying() ?? false;
    }

    /**
     * Whether a card's button press, such as Apply, is still waiting for the server.
     */
    isChangeSetRequestPending(): boolean {
        return this.changeSetCards?.isBusy() ?? false;
    }

    /**
     * By change set, the changes touching participant input the user ticked, sent with a typed confirmation.
     */
    confirmedChanges(sets: PageChangeSetStatusItem[]): Record<string, string[]> {
        return Object.fromEntries(sets
            .map(set => [set.id, this.confirmations.confirmed(set)] as const)
            .filter(([, ids]) => ids.length > 0));
    }

    explainNewChatWaits(): void {
        this.changeSetCards?.explainNewChatWaits();
    }

    refreshChangeSets(): void {
        this.changeSetCards?.refresh();
        this.pagePreview?.render(this.lastStatusItems);
    }

    private renderChangeSets(statusItems: ChatStatusItem[]): void {
        this.lastStatusItems = statusItems;
        this.changeSetCards?.render(statusItems);
        this.pagePreview?.render(statusItems);
    }

    destroy(): void {
        this.pagePreview?.destroy();
        this.scrollController.destroy();
        this.transcriptRenderer.destroy();
        this.root.remove();
    }

    getDraft(): string {
        return this.textarea.value;
    }

    setDraft(message: string): void {
        this.textarea.value = message;
        this.resizeTextarea();
        this.updateSubmitState();
    }

    startQueuedRunNoticeTimer(): void {
        this.transcriptRenderer.startQueuedRunNoticeTimer();
    }

    clearQueuedRunNotice(): void {
        this.transcriptRenderer.clearQueuedRunNotice();
    }

    /**
     * Stops the chat from sending and says why above the composer, where the user would otherwise type. The
     * transcript and any draft stay as they are. What invites the user to type, the welcome and the placeholder,
     * goes, so the chat does not ask for a message it cannot send.
     */
    pause(notice: string): void {
        this.paused = true;
        this.root.dataset.paused = 'true';
        this.textarea.placeholder = '';
        if (this.pausedNotice === null) {
            this.pausedNotice = document.createElement('div');
            this.pausedNotice.className = 'howspace-ai-chat-warning';
            this.pausedNotice.setAttribute('role', 'status');
            this.form.before(this.pausedNotice);
        }
        this.pausedNotice.textContent = notice;
        this.suggestions.replaceChildren();
        this.suggestions.hidden = true;
        this.updateSubmitState();
    }

    isPaused(): boolean {
        return this.paused;
    }

    private setRunning(running: boolean): void {
        this.running = running;
        if (!running) {
            this.transcriptRenderer.clearQueuedRunNotice();
        }
        const value = String(running);
        if (this.root.dataset.running !== value) {
            this.root.dataset.running = value;
        }
    }

    /**
     * With onlyWhenIdle the composer takes focus only if nothing else has it yet. A chat that appears with the
     * page should not pull a keyboard user out of whatever they already moved on to.
     */
    focusComposer(onlyWhenIdle = false): void {
        window.requestAnimationFrame(() => {
            const idle = document.activeElement === null || document.activeElement === document.body;
            if (this.root.isConnected && (idle || !onlyWhenIdle)) {
                this.textarea.focus({preventScroll: true});
            }
        });
    }

    /**
     * Puts text into the composer for the user to edit or send. Anything already written stays, with the new
     * text on a line of its own after it.
     */
    fillComposer(text: string): void {
        const draft = this.textarea.value.trim();
        this.textarea.value = draft === '' ? text : draft + '\n' + text;
        this.resizeTextarea();
        this.updateSubmitState();
        this.focusComposer();
        const end = this.textarea.value.length;
        this.textarea.setSelectionRange(end, end);
    }

    // Styling hook for the add-context button: it keeps its labelled form until the user has actually
    // given the agent material to work with. Uploads that are still processing or that failed do not count.
    private setContextMaterialPresent(statusItems: ChatStatusItem[]): void {
        const present = statusItems.some(
            item => isWorkspaceContextUploadStatusItem(item) && item.status === 'ready',
        );
        this.root.dataset.hasContextMaterial = String(present);
    }

    private setContextUploadBusy(busy: boolean): void {
        if (this.contextUploadBusy === busy) {
            return;
        }

        this.contextUploadBusy = busy;
        this.root.dataset.contextUploadBusy = String(busy);
        this.updateSubmitState();
    }

    scrollToBottom(): void {
        this.scrollController.scrollToBottom();
    }

    revealLatestChangeSet(): void {
        this.changeSetCards?.revealLatest();
    }

    private resizeTextarea(): void {
        this.textarea.style.height = 'auto';
        this.textarea.style.height = `${this.textarea.scrollHeight}px`;
    }

    private updateSubmitState(): void {
        const unavailable = this.paused || isActivityIdle();
        if (this.textarea.disabled !== unavailable) {
            this.textarea.disabled = unavailable;
        }

        // A file that is still converting no longer holds the conversation up. The agent is told which files
        // are still processing, so it answers a question about one by saying so instead of by saying it has
        // no file, and the interview carries on meanwhile.
        const disabled = unavailable || this.running || this.textarea.value.trim() === '';
        if (this.submitButton.disabled !== disabled) {
            this.submitButton.disabled = disabled;
        }

        if (this.addContextButton !== null) {
            const addDisabled = unavailable || this.running || this.contextUploadBusy;
            if (this.addContextButton.disabled !== addDisabled) {
                this.addContextButton.disabled = addDisabled;
            }
        }
    }
}

const isActivityIdle = (): boolean => (
    typeof howspace !== 'undefined'
        && typeof howspace.activityTracker?.isIdle === 'function'
        && howspace.activityTracker.isIdle()
);
